import type { UiMenuOptionType } from "@dpa-id-components/dpa-shared-components/src/components/UiMenu/UiMenu.vue";
import { endOfDay, startOfDay } from "date-fns";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import useFilter from "@/composition/archive/useFilter.ts";
import useFilterTermSet from "@/composition/archive/useFilterTermSet.ts";
import { FilterType, RelativeDateRange } from "@/types/enums.ts";

import { useSearchStore } from "../search.ts";

import useSavedSearchesStore from "./savedSearches.ts";

export type Filter = {
  label: string;
  options: UiMenuOptionType<string>[];
};

/**
 * Cover full local days (00:00:00.000 to
 * 23:59:59.999), discarding whatever time-of-day the datepicker reports for
 * the clicked day.
 */
export function normalizeDateRangeToFullDays(
  dateRange: [Date, Date],
): [Date, Date] {
  return [startOfDay(dateRange[0]), endOfDay(dateRange[1])];
}

// TODO: When playwright is merged write a new E2E Test that tests functionality of multiple filters adding and removal

/**
 * Store for the selection of Filters
 * Steps to add a filter:
 * 1. Create a new FilterType in the enum
 * 2. Make sure the respected FilterType is returned from the API
 * 3. Use the useFilter composable to get the data and functions needed for a working filter
 * 4. Add the new filter to the activeFilters computed property
 * 5. Create a new Component to reflect the filter, if used in the sidebar it should use the FilterSelectorItem component
 */
const useFilterSelectorStore = defineStore("search:filterSelector", () => {
  const searchStore = useSearchStore();
  const route = useRoute();

  const data = useFilterTermSet();

  const {
    selected: selectedProducts,
    filterMenu: productFilter,
    resetSelectedFilters: resetSelectedProducts,
  } = useFilter(FilterType.PRODUCT, {
    filterData: data,
  });

  const {
    selected: selectedCategories,
    filterMenu: categoryFilter,
    resetSelectedFilters: resetSelectedCategories,
  } = useFilter(FilterType.CATEGORY, {
    filterData: data,
  });

  const {
    selected: selectedMediaTypes,
    filterMenu: mediaTypeFilter,
    resetSelectedFilters: resetSelectedMediaType,
  } = useFilter(FilterType.MEDIATYPE, { filterData: data });

  const {
    selected: selectedUrgencies,
    filterMenu: urgencyFilter,
    resetSelectedFilters: resetSelectedUrgencies,
  } = useFilter(FilterType.URGENCY, {
    filterData: data,
  });

  const {
    selected: selectedGenres,
    filterMenu: genreFilter,
    resetSelectedFilters: resetSelectedGenres,
  } = useFilter(FilterType.GENRE, {
    filterData: data,
  });

  const {
    selected: selectedStatusTags,
    filterMenu: statusTagsFilter,
    resetSelectedFilters: resetStatusTags,
  } = useFilter(FilterType.STATUS_TAGS, {
    filterData: data,
  });

  const {
    selected: selectedUserNeeds,
    filterMenu: userNeedFilter,
    resetSelectedFilters: resetSelectedUserNeeds,
  } = useFilter(FilterType.USER_NEED, {
    filterData: data,
  });

  const {
    selected: selectedTShirtSizes,
    filterMenu: tShirtSizeFilter,
    resetSelectedFilters: resetSelectedTShirtSizes,
  } = useFilter(FilterType.T_SHIRT_SIZE, {
    filterData: data,
  });

  const {
    selected: selectedProfiles,
    filterMenu: profileFilter,
    resetSelectedFilters: resetSelectedProfiles,
  } = useFilter(FilterType.FILTER_DPA_IQ_PROFILE_ID, {
    filterData: data,
  });

  function transformValues(values: string[], type: FilterType) {
    return values.map(
      (value) =>
        data.value?.[type].find((filter) => filter.key === value)?.terms ?? [],
    );
  }

  const selectedRelativeDate = ref<[RelativeDateRange, string] | undefined>(
    undefined,
  );
  const selectedDate = ref<[Date, Date] | undefined>(undefined);

  function setSelectedDate(
    newDate: [Date, Date],
    resetActiveSavedSearchId: boolean = true,
  ) {
    if (resetActiveSavedSearchId) {
      savedSearchesStore.resetActiveSavedSearchId();
    }

    if (searchStore.setAutoRefresh) {
      searchStore.setAutoRefresh(false);
    }
    resetSelectedRelativeDate();
    selectedDate.value = normalizeDateRangeToFullDays(newDate);
  }

  function setSelectedRelativeDate(
    newDate: [RelativeDateRange, string],
    resetActiveSavedSearchId = true,
  ) {
    if (resetActiveSavedSearchId) {
      savedSearchesStore.resetActiveSavedSearchId();
    }
    if (
      (searchStore.setAutoRefresh &&
        newDate[0] === RelativeDateRange.LAST_YEAR) ||
      newDate[0] === RelativeDateRange.LAST_MONTH
    ) {
      searchStore.setAutoRefresh(false);
    }
    resetSelectedDate();
    selectedRelativeDate.value = newDate;
  }

  function resetSelectedDates() {
    searchStore.setAutoRefresh(true);
    selectedDate.value = undefined;
    selectedRelativeDate.value = undefined;
  }

  function resetSelectedRelativeDate() {
    selectedRelativeDate.value = undefined;
  }

  function resetSelectedDate() {
    selectedDate.value = undefined;
  }

  const activeFilters = computed(() => {
    return [
      {
        type: FilterType.CATEGORY,
        metaDataTags: Array.from(selectedCategories.value),
        values: transformValues(selectedCategories.value, FilterType.CATEGORY),
      },
      {
        type: FilterType.PRODUCT,
        metaDataTags: Array.from(selectedProducts.value),
        values: transformValues(selectedProducts.value, FilterType.PRODUCT),
      },
      {
        type: FilterType.MEDIATYPE,
        metaDataTags: Array.from(selectedMediaTypes.value),
        values: transformValues(selectedMediaTypes.value, FilterType.MEDIATYPE),
      },
      {
        type: FilterType.URGENCY,
        metaDataTags: Array.from(selectedUrgencies.value),
        values: transformValues(selectedUrgencies.value, FilterType.URGENCY),
      },
      {
        type: FilterType.GENRE,
        metaDataTags: Array.from(selectedGenres.value),
        values: transformValues(selectedGenres.value, FilterType.GENRE),
      },
      {
        type: FilterType.USER_NEED,
        metaDataTags: Array.from(selectedUserNeeds.value),
        values: transformValues(selectedUserNeeds.value, FilterType.USER_NEED),
      },
      {
        type: FilterType.T_SHIRT_SIZE,
        metaDataTags: Array.from(selectedTShirtSizes.value),
        values: transformValues(
          selectedTShirtSizes.value,
          FilterType.T_SHIRT_SIZE,
        ),
      },
      {
        type: FilterType.STATUS_TAGS,
        metaDataTags: Array.from(selectedStatusTags.value),
        values: transformValues(
          selectedStatusTags.value,
          FilterType.STATUS_TAGS,
        ),
      },
      {
        type: FilterType.FILTER_DPA_IQ_PROFILE_ID,
        metaDataTags: Array.from(selectedProfiles.value),
        values: transformValues(
          selectedProfiles.value,
          FilterType.FILTER_DPA_IQ_PROFILE_ID,
        ),
      },
      {
        type: FilterType.DATE,
        metaDataTags: selectedDate.value?.map((date) => date.toISOString()),
        values: selectedDate.value?.map((date) => date.toISOString()),
      },
      {
        type: FilterType.RELATIVE_DATE,
        metaDataTags: selectedRelativeDate.value
          ? [...selectedRelativeDate.value]
          : [],
        values: selectedRelativeDate.value
          ? [...selectedRelativeDate.value]
          : [],
      },
    ];
  });

  const routeQuery = computed(() => {
    return activeFilters.value.reduce((currentValue, nextValue) => {
      if (nextValue.values?.length === 0) return currentValue;
      return {
        ...currentValue,
        [nextValue.type]: nextValue.metaDataTags,
      };
    }, {});
  });

  const savedSearchesStore = useSavedSearchesStore();
  function resetFilters() {
    resetSelectedProducts();
    resetSelectedCategories();
    resetSelectedMediaType();
    resetSelectedUrgencies();
    resetSelectedGenres();
    resetStatusTags();
    resetSelectedDates();
    resetSelectedUserNeeds();
    resetSelectedTShirtSizes();
    resetSelectedProfiles();
    savedSearchesStore.resetActiveSavedSearchId();
  }

  const hasAnyActiveFilter = computed(() => {
    return activeFilters.value.some((filter) => !!filter.values?.length);
  });

  // Should be executed once when app is mounted to apply date filters from URL
  watch(
    () => route.query,
    (newVal) => {
      if (newVal[FilterType.DATE]) {
        const initialDateQuery = newVal[FilterType.DATE];
        // use type assertion here as converting it from the qs library doesn't really work
        const dateValue = initialDateQuery as string[];
        if (Array.isArray(dateValue)) {
          const convertedDate = dateValue.map((date) => new Date(date));
          selectedDate.value = convertedDate as [Date, Date];
        }
      }
      if (route.query[FilterType.RELATIVE_DATE]) {
        const relativeDate = route.query[FilterType.RELATIVE_DATE];
        selectedRelativeDate.value = relativeDate as [
          RelativeDateRange,
          string,
        ];
      }
    },
    { immediate: true },
  );
  return {
    activeFilters,
    routeQuery,

    productFilter,
    selectedProducts,
    selectedCategories,
    categoryFilter,
    mediaTypeFilter,
    selectedMediaTypes,
    selectedUrgencies,
    urgencyFilter,
    selectedGenres,
    genreFilter,
    selectedStatusTags,
    statusTagsFilter,
    selectedDate,
    selectedRelativeDate,
    userNeedFilter,
    selectedUserNeeds,
    tShirtSizeFilter,
    selectedTShirtSizes,
    profileFilter,
    selectedProfiles,
    resetSelectedDates,
    resetSelectedDate,
    resetSelectedRelativeDate,
    setSelectedDate,
    setSelectedRelativeDate,
    resetFilters,
    hasAnyActiveFilter,
    filterTermSet: data,
  };
});

export default useFilterSelectorStore;
